import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';
import { getEffectiveImageConfig, getImageModelIdsByTag, IMAGE_MODEL_TAGS, imageModels } from '@flowmuse/models/image';
import { useModelLabelById } from '@/lib/models/use-model-label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Pencil, Sparkles, Scissors, ScanSearch, Expand, Loader2Icon, Eye, Grid2X2, SlidersHorizontal, Brush } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { handleError } from '@/lib/error/handle';
import { uploadFile } from '@/lib/upload';
import { useAuth, useUser } from '@clerk/nextjs';
import { useProject } from '@/providers/project';
import { toast } from 'sonner';
import { useImageGridSplit } from '@/components/nodes/image/image-grid-split';
import RepaintDialog, { REPAINT_MODEL_IDS } from '@/components/nodes/image/repaint-dialog';
import { openPinturaImageModal } from '@/components/nodes/pintura/open';
import {
  ConfirmFooter,
  getToolbarBtnCls,
  MoreToolPopover,
  SectionLabel,
  SegmentedRow,
  ToolbarActionButton,
  ToolListItem,
  TOOLBAR_POPOVER_CONTENT_CLS,
  TOOLBAR_SURFACE_CLS,
  TopToolbarShell,
  useCreateNeighborNode,
  useTopToolbarState,
} from '@/components/nodes/shared/top-toolbar-kit';

// 顶部工具栏各功能接入的模型：通过模型配置 tags 解析，避免硬编码模型 ID。
// 模型注册表是静态的，模块级解析一次即可。
const ENHANCE_MODEL_IDS = getImageModelIdsByTag(IMAGE_MODEL_TAGS.upscale);
const EFFECTS_MODEL_IDS = getImageModelIdsByTag(IMAGE_MODEL_TAGS.effects);
const REMOVE_BG_MODEL_IDS = getImageModelIdsByTag(IMAGE_MODEL_TAGS.removeBackground);
const REFRAME_MODEL_IDS = getImageModelIdsByTag(IMAGE_MODEL_TAGS.reframe);

// 比例小图标：统一用横版/竖版/正方形三种固定形状，让用户快速感知方向
function RatioIcon({ size }: { size: string }) {
  const [w, h] = size.split('x').map(Number);
  if (!w || !h) return null;
  const [rw, rh] = w > h ? [13, 9] : w < h ? [9, 13] : [11, 11];
  return (
    <span
      className="inline-block shrink-0 rounded-[3px] border-[1.5px] border-current opacity-60"
      style={{ width: rw, height: rh }}
    />
  );
}

type ModelToolPopoverProps = {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  triggerCls: string;
  contentCls: string;
  modelIds: string[];
  disabled: boolean;
  /** 是否显示目标比例选择（如比例重构） */
  withSizes?: boolean;
  modelLabel: (id: string) => string;
  t: (key: string) => string;
  onConfirm: (modelId: string, opts: { size?: string; resolution?: string }) => void;
};

/**
 * 工具栏通用「选模型 -> （可选比例/分辨率）-> 确认」弹层。
 * 模型列表来自 tag 解析；分辨率/比例选项跟随所选模型的 i2i 配置。
 */
function ModelToolPopover({
  icon: Icon,
  label,
  triggerCls,
  contentCls,
  modelIds,
  disabled,
  withSizes,
  modelLabel,
  t,
  onConfirm,
}: ModelToolPopoverProps) {
  const [modelId, setModelId] = useState<string | undefined>(modelIds[0]);
  const modelCfg = useMemo(
    () => getEffectiveImageConfig(modelId ? (imageModels as any)[modelId] : undefined, 'i2i'),
    [modelId],
  );
  const resolutions = (modelCfg.resolutions as string[] | undefined) ?? [];
  const sizes = useMemo<string[]>(() => {
    if (!withSizes) return [];
    const cfgSizes = ((modelCfg.sizes as string[] | undefined) ?? []).filter((s) => s !== 'match');
    return cfgSizes.length > 0 ? cfgSizes : ['1x1', '16x9', '9x16', '3x4', '4x3'];
  }, [withSizes, modelCfg]);
  const [size, setSize] = useState<string | undefined>(undefined);
  const [resolution, setResolution] = useState<string | undefined>(resolutions[0]);
  // 切换模型时重置为该模型的第一个分辨率档位
  useEffect(() => {
    setResolution(((modelCfg.resolutions as string[] | undefined) ?? [])[0]);
  }, [modelCfg]);

  if (modelIds.length === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <ToolbarActionButton icon={Icon} label={label} className={triggerCls} disabled={disabled} />
      </PopoverTrigger>
      <PopoverContent align="start" className={contentCls}>
        <SectionLabel>{t('Choose model')}</SectionLabel>
        <div className="mb-2 flex flex-col gap-1">
          {modelIds.map((mid) => (
            <ToolListItem key={mid} selected={modelId === mid} onClick={() => setModelId(mid)}>
              {modelLabel(mid)}
            </ToolListItem>
          ))}
        </div>
        {withSizes && sizes.length > 0 && (
          <>
            <SectionLabel>{t('Choose size')}</SectionLabel>
            <div className="grid grid-cols-4 gap-1 mb-3">
              {sizes.map((sz) => (
                <Button
                  key={sz}
                  size="sm"
                  variant="ghost"
                  className={cn(
                    "h-9 px-1 gap-1 text-[11px] font-medium rounded-lg border transition-all",
                    size === sz ? "bg-primary/10 border-primary/20 text-primary" : "bg-sidebar-accent/30 border-transparent hover:bg-sidebar-accent"
                  )}
                  onClick={() => setSize(sz)}
                >
                  <RatioIcon size={sz} />
                  {sz.replace('x', ':')}
                </Button>
              ))}
            </div>
          </>
        )}
        {resolutions.length > 0 && (
          <>
            <SectionLabel>{t('Resolution')}</SectionLabel>
            <SegmentedRow options={resolutions} value={resolution} onChange={setResolution} />
          </>
        )}
        <ConfirmFooter
          disabled={disabled || !modelId || (withSizes && !size)}
          onConfirm={() => {
            if (!modelId) return;
            onConfirm(modelId, { size, resolution });
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

type ImageTopToolbarProps = {
  nodeId: string;
  imageUrl?: string;
  data: any;
  isToolbarBlocked: boolean;
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  isDescribing: boolean;
  hasDescription: boolean;
  onShowDescription: () => void;
  onParse: () => void;
};

export function ImageTopToolbar({
  nodeId,
  imageUrl,
  isToolbarBlocked,
  isEditing,
  setIsEditing,
  isDescribing,
  hasDescription,
  onShowDescription,
  onParse,
}: ImageTopToolbarProps) {
  const { t, i18n } = useI18n();
  const isZh = (i18n?.language || '').toLowerCase().startsWith('zh');
  const { getToken } = useAuth();
  const { user } = useUser();
  const project = useProject();
  const gridSplit = useImageGridSplit({
    nodeId,
    imageUrl,
    disabled: isToolbarBlocked,
    t,
  });
  const [splitOpen, setSplitOpen] = useState(false);
  const [repaintOpen, setRepaintOpen] = useState(false);

  const {
    isMultiSelect,
    shown: topToolbarShown,
    pointerEnabled: topToolbarPointerEnabled,
  } = useTopToolbarState(nodeId, Boolean(imageUrl));

  // 模型展示名：数据库 model_access.labels，未配置时回退到模型 ID
  const getLabelById = useModelLabelById();
  const modelLabel = useCallback(
    (mid: string) => getLabelById(mid) ?? mid,
    [getLabelById],
  );

  // Edit image with Pintura
  const handleEditImage = async () => {
    if (!imageUrl) return;
    try {
      setIsEditing(true);
      const edited = await openPinturaImageModal({ src: imageUrl });
      if (!edited) return;
      const token = await getToken({ template: 'supabase' });
      if (!token || !user?.id) {
        try { toast.error(t('Please sign in to upload')); } catch {}
        return;
      }
      const file = new File([edited.blob], `edit-${Date.now()}.png`, { type: 'image/png' });
      const uploaded = await uploadFile(file, 'files', {
        token,
        userId: user.id,
        projectId: String(project?.id || ''),
        assetType: 'image',
      });
      // delegate update to parent is complex; do minimal event
      window.dispatchEvent(new CustomEvent('image_node_updated', {
        detail: { id: nodeId, url: uploaded.url, type: uploaded.type, width: edited.width, height: edited.height, size: edited.size },
      }));
      try { toast.success(t('Image updated')); } catch {}
    } catch (e) {
      handleError(t('Failed to edit image'), e);
    } finally {
      setIsEditing(false);
    }
  };

  const createNeighbor = useCreateNeighborNode(nodeId);
  const createNeighborNode = (modelId: string, extraData?: Record<string, any>) =>
    createNeighbor('image', modelId, extraData);

  if (!imageUrl || isMultiSelect) return null;

  // 隐藏时不构建弹层 JSX 树，避免画布上每个节点的无谓渲染开销
  const toolbarButtons = !topToolbarShown ? null : (
    <div className={TOOLBAR_SURFACE_CLS}>
      {(() => {
        const btnCls = getToolbarBtnCls(isZh);
        const popoverContentCls = TOOLBAR_POPOVER_CONTENT_CLS;
        return (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              {/* 画质增强（模型来自 tag: image-upscale） */}
              <ModelToolPopover
                icon={Sparkles}
                label={t('Enhance quality')}
                triggerCls={btnCls}
                contentCls={popoverContentCls}
                modelIds={ENHANCE_MODEL_IDS}
                disabled={isToolbarBlocked}
                modelLabel={modelLabel}
                t={t}
                onConfirm={(mid, { resolution }) =>
                  createNeighborNode(mid, resolution ? { image_resolution: resolution } : undefined)
                }
              />
              {/* 图像处理（模型来自 tag: image-effects） */}
              <ModelToolPopover
                icon={SlidersHorizontal}
                label={t('Image processing')}
                triggerCls={btnCls}
                contentCls={popoverContentCls}
                modelIds={EFFECTS_MODEL_IDS}
                disabled={isToolbarBlocked}
                modelLabel={modelLabel}
                t={t}
                onConfirm={(mid, { resolution }) =>
                  createNeighborNode(mid, resolution ? { image_resolution: resolution } : undefined)
                }
              />
              {/* 一键抠图（模型来自 tag: image-remove-background） */}
              <ModelToolPopover
                icon={Scissors}
                label={t('Remove background')}
                triggerCls={btnCls}
                contentCls={popoverContentCls}
                modelIds={REMOVE_BG_MODEL_IDS}
                disabled={isToolbarBlocked}
                modelLabel={modelLabel}
                t={t}
                onConfirm={(mid, { resolution }) =>
                  createNeighborNode(mid, resolution ? { image_resolution: resolution } : undefined)
                }
              />
              {/* 比例重构（模型来自 tag: image-reframe，需选目标比例） */}
              <ModelToolPopover
                icon={Expand}
                label={t('Reframe')}
                triggerCls={btnCls}
                contentCls={popoverContentCls}
                modelIds={REFRAME_MODEL_IDS}
                disabled={isToolbarBlocked}
                withSizes
                modelLabel={modelLabel}
                t={t}
                onConfirm={(mid, { size, resolution }) =>
                  createNeighborNode(mid, {
                    size,
                    ...(resolution ? { image_resolution: resolution } : {}),
                  })
                }
              />
              {/* 图像重绘（模型来自 tag: image-inpaint，画笔勾选重绘区域） */}
              {REPAINT_MODEL_IDS.length > 0 && (
                <ToolbarActionButton
                  icon={Brush}
                  label={t('Image repaint')}
                  className={btnCls}
                  disabled={isToolbarBlocked}
                  onClick={() => setRepaintOpen(true)}
                />
              )}
              {/* 图像分割（样式与画质增强一致） */}
              <Popover open={splitOpen} onOpenChange={(o) => { if (!gridSplit.isSplitting) setSplitOpen(o); }}>
                <PopoverTrigger asChild>
                  <ToolbarActionButton
                    icon={Grid2X2}
                    label={t('Split image')}
                    className={btnCls}
                    disabled={isToolbarBlocked}
                  />
                </PopoverTrigger>
                <PopoverContent align="start" className={popoverContentCls}>
                  <SectionLabel>{t('Split image')}</SectionLabel>
                  <div className="mb-2 flex flex-col gap-1">
                    {(['2x2', '3x3', '4x4', '5x5', 'custom'] as const).map((p) => {
                      const label = p === 'custom'
                        ? t('Custom')
                        : (() => {
                            const [r, c] = p.split('x').map(Number);
                            const credits = gridSplit.getSplitCredits(r, c);
                            return `${r}×${c}${credits > 0 ? t('Credits price label', { count: credits }) : t('Free label')}`;
                          })();
                      return (
                        <ToolListItem
                          key={p}
                          selected={gridSplit.preset === p}
                          disabled={gridSplit.isSplitting}
                          onClick={() => gridSplit.setPreset(p)}
                        >
                          {label}
                        </ToolListItem>
                      );
                    })}
                  </div>
                  {gridSplit.preset === 'custom' ? (
                    <div className="mb-2 rounded-lg border border-sidebar-border/30 bg-sidebar-accent/20 p-2 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <div className="text-[10px] text-muted-foreground">{t('Rows')}</div>
                          <Input
                            value={gridSplit.customRowsText}
                            onChange={(e) => gridSplit.setCustomRowsText(e.target.value)}
                            inputMode="numeric"
                            disabled={gridSplit.isSplitting}
                            placeholder="2"
                            className="h-8 text-xs"
                          />
                        </div>
                        <div className="space-y-1">
                          <div className="text-[10px] text-muted-foreground">{t('Columns')}</div>
                          <Input
                            value={gridSplit.customColsText}
                            onChange={(e) => gridSplit.setCustomColsText(e.target.value)}
                            inputMode="numeric"
                            disabled={gridSplit.isSplitting}
                            placeholder="2"
                            className="h-8 text-xs"
                          />
                        </div>
                      </div>
                      <div className="text-[10px] text-muted-foreground text-center">
                        {gridSplit.customValid
                          ? `${gridSplit.customRows}×${gridSplit.customCols}${
                              gridSplit.customCredits > 0
                                ? t('Credits price label', { count: gridSplit.customCredits })
                                : t('Free label')
                            }`
                          : t('Custom grid hint')}
                      </div>
                    </div>
                  ) : null}
                  <ConfirmFooter
                    disabled={isToolbarBlocked || gridSplit.isSplitting || (gridSplit.preset === 'custom' && !gridSplit.customValid)}
                    onConfirm={async () => {
                      let ok = false;
                      if (gridSplit.preset === 'custom') {
                        ok = await gridSplit.run(gridSplit.customRows, gridSplit.customCols);
                      } else {
                        const [r, c] = gridSplit.preset.split('x').map(Number);
                        ok = await gridSplit.run(r!, c!);
                      }
                      if (ok) setSplitOpen(false);
                    }}
                  >
                    {gridSplit.isSplitting ? <Loader2Icon size={14} className="mr-1.5 animate-spin" /> : null}
                    {t('Confirm')}
                  </ConfirmFooter>
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex items-center gap-1">
              {/* 预览 */}
              <ToolbarActionButton
                icon={Eye}
                label={t('Preview')}
                className={btnCls}
                disabled={isToolbarBlocked}
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('open_image_preview', { detail: { id: nodeId } }));
                  } catch {}
                }}
              />
              {/* 图片编辑器 */}
              <ToolbarActionButton
                icon={Pencil}
                label={t('Image Editor')}
                className={btnCls}
                loading={isEditing}
                disabled={isToolbarBlocked || isEditing}
                onClick={handleEditImage}
              />
              {/* 图像解析 */}
              <ToolbarActionButton
                icon={ScanSearch}
                label={t('Parse image')}
                className={btnCls}
                loading={isDescribing && !hasDescription}
                disabled={!hasDescription && (isToolbarBlocked || isDescribing)}
                onClick={() => {
                  if (hasDescription) onShowDescription();
                  else onParse();
                }}
              />
              {/* 更多 */}
              <MoreToolPopover nodeId={nodeId} url={imageUrl} disabled={isToolbarBlocked} triggerCls={btnCls} />
            </div>
          </div>
        );
      })()}
    </div>
  );

  return (
    <>
      <TopToolbarShell shown={topToolbarShown} pointerEnabled={topToolbarPointerEnabled}>
        {toolbarButtons}
      </TopToolbarShell>
      {/* 图像重绘弹窗：画笔勾选区域 -> 创建带遮罩的重绘节点 */}
      {repaintOpen ? (
        <RepaintDialog
          open={repaintOpen}
          onOpenChange={setRepaintOpen}
          imageUrl={imageUrl}
          onRedraw={async (mask, prompt, modelId, resolution) => {
            try {
              // 遮罩先上传为图片文件，节点上只存 URL（避免 base64 跟随节点数据保存/传输）
              const token = await getToken({ template: 'supabase' });
              if (!token || !user?.id) {
                try { toast.error(t('Please sign in to upload')); } catch {}
                return;
              }
              const blob = await (await fetch(mask)).blob();
              const file = new File([blob], `mask-${Date.now()}.png`, { type: 'image/png' });
              const uploaded = await uploadFile(file, 'files', {
                token,
                userId: user.id,
                projectId: String(project?.id || ''),
                assetType: 'image',
              });
              createNeighborNode(modelId, {
                instructions: prompt,
                inpaintMask: uploaded.url,
                inpaintPrompt: prompt,
                ...(resolution ? { image_resolution: resolution } : {}),
              });
            } catch (e) {
              handleError(t('Request failed'), e);
            }
          }}
        />
      ) : null}
    </>
  );
}


